import {GameStateEnums} from '../../../../../shared/GameEnums'
import {Ease, PIXI} from '@colonist/pixi'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {Sound} from '../../Sound'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {UIGameDiceView} from './UIGameDiceView'
import {UIGamePlayerInformationView} from '../ui_game_players/UIGamePlayerInformationView'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'
import {UIGameBeginnerHintView} from '../ui_game_beginner_hints/UIGameBeginnerHintView'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {UIGameBeginnerModeDiceHelper} from '../ui_game_beginner_mode/ui_game_beginner_mode_helpers/UIGameBeginnerModeDiceHelper'
import {ActionState, PlayerColor, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGameDiceController implements UIGameMechanic {
    private readonly animationTime: number = 300
    private currentTurnPlayer: PlayerColor | undefined

    protected readonly uiGameManager: UIGameManager
    protected readonly easeInAnimation: Ease
    protected easeAnimationCount: number = 0 // When the dice starts animating, allows the dice to ease into the animation
    protected isPlayerDiceAnimating: boolean

    protected playerDiceView: UIGameDiceView
    protected opponentDiceViews: UIGameDiceView[]
    private currentDiceView: UIGameDiceView

    private readonly isSinglePlayerGame: boolean = false
    private beginnerHintView?: UIGameBeginnerHintView | undefined

    private beginnerModeHelper?: UIGameBeginnerModeDiceHelper

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.isPlayerDiceAnimating = false
        this.easeInAnimation = new Ease({useRef: false})
        this.isSinglePlayerGame = this.uiGameManager.gameState.players.length == 1
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.DiceStateUpdated: this.diceStateUpdated(); break
            case UIGameEventEnums.GameStateUpdated: this.gameStateUpdated(); break
            case UIGameEventEnums.CanvasResized: this.redrawDiceViews(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.SelectDice: this.stopAnimation(); break
            default: break
        }
    }

    initBeginnerModeHelper() {
        this.beginnerModeHelper = new UIGameBeginnerModeDiceHelper(this.uiGameManager)
    }

    protected abstract initializePlayerDiceView()

    protected abstract initializeOpponentDiceViews()

    protected initializeCurrentTurnPlayer() {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement) return
        this.currentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
    }

    protected redrawDiceViews() {
        const hintActive = this.beginnerHintView != undefined
        this.playerDiceView.destroy()
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.destroy())
        this.initializeCurrentTurnPlayer()
        this.destroyBeginnerHintView()
        this.initializePlayerDiceView()
        this.initializeOpponentDiceViews()
        this.updateDiceView()
        if(hintActive) this.beginnerHintReceived({type: GameAction.ClickedDice, data: {}})
        if(this.isPlayerDiceAnimating) this.playerDiceView.tintActive()
    }

    protected startPlayerDiceAnimation() {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.GameEnd) this.stopAnimation()
        if(!this.uiGameManager.gameController.isMyTurn) return
        this.playerDiceView.easeIn(this.animationTime)
        this.currentDiceView = this.playerDiceView
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) return
        if(this.uiGameManager.gameController.diceState.diceThrown) return

        this.isPlayerDiceAnimating = true
        this.playerDiceView.animate()
        this.playerDiceView.tintActive()
    }

    protected diceStateUpdated() {
        this.updateDiceImage()
    }

    protected updateDiceImage() {
        this.updateDiceTexture()
        if(!this.uiGameManager.gameController.diceState.diceThrown) return
        this.stopAnimation()

        if(this.uiGameManager.gameController.isMyTurn) return this.startDiceNumberAnimation(this.playerDiceView)
        for(const view of this.opponentDiceViews) this.startDiceNumberAnimation(view)
    }

    private gameStateUpdated() {
        this.destroyBeginnerHintView()

        const shouldHidePlayerDice = this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement || this.beginnerModeHelper?.isDiceHiddenPermanently
        if(shouldHidePlayerDice) return this.hideAllDiceViews()

        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) return

        if(this.shouldUpdateDiceView() == false) return

        const currentTurnPlayerColor = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        this.currentTurnPlayer = currentTurnPlayerColor
        this.hideCurrentDiceView()
        this.updateDiceView()
    }

    private shouldUpdateDiceView(): boolean {
        const currentTurnPlayerColor = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        if(this.isSinglePlayerGame && this.uiGameManager.gameController.isMyTurn) return true
        if(this.beginnerModeHelper != undefined) return true
        if(currentTurnPlayerColor == this.currentTurnPlayer) return false
        return true
    }

    private hideCurrentDiceView() {
        if(this.currentDiceView == undefined) return
        this.currentDiceView.easeOut(this.animationTime)
    }

    protected updateDiceView() {
        this.uiGameManager.gameController.isMyTurn ? this.startPlayerDiceAnimation() : this.makeOpponentDiceVisible()
    }

    private hideAllDiceViews() {
        this.playerDiceView.hide()
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.hide())
    }

    protected makeOpponentDiceVisible() {
        if(this.opponentDiceViews.length == 0) return

        const playerInformationViews = this.uiGameManager.playersController.playerInformationViews
        const currentTurnPlayerIndex = playerInformationViews.findIndex((view: UIGamePlayerInformationView) => view.player.color == this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        const opponentCount = playerInformationViews.length - 1
        if(this.uiGameManager.gameController.isPlayerSpectator && currentTurnPlayerIndex == opponentCount) return

        const opponentDiceViewToDisplay = this.getOpponentDiceViewToDisplay(currentTurnPlayerIndex, opponentCount)
        if(this.currentDiceView == opponentDiceViewToDisplay) this.easeInAnimation.removeAll()
        this.currentDiceView = opponentDiceViewToDisplay
        opponentDiceViewToDisplay.easeIn(this.animationTime)
    }

    protected stopAnimation() {
        this.isPlayerDiceAnimating = false
        this.easeAnimationCount = 0
        this.playerDiceView.resetDice()
    }

    protected updateDiceTexture() {
        this.playerDiceView.updateDiceTexture()
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.updateDiceTexture())
    }

    protected clickedDice(view: PIXI.Container) {
        Sound.playSound(Sound.SoundType.Click)

        if(!this.uiGameManager.gameController.isMyTurn) {
            new FloatingText({key: 'strings:game.errors.notYourTurn'}, view)
            return
        }
        const isBeginnerMode = this.beginnerModeHelper != undefined
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice && isBeginnerMode) {
            this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.BeginnerModeClickedDisabledElement)
            return
        }
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) {
            new FloatingText({key: 'strings:game.errors.diceAlreadyRolled'}, view)
            return
        }
        if(this.currentActionStateIsEmpty() == false) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, view)
            return
        }

        this.uiGameManager.socketGameSend.clickedDice()
    }

    protected currentActionStateIsEmpty(): boolean {
        return this.uiGameManager.gameController.currentState.actionState == ActionState.None
    }

    protected getOpponentDiceViewToDisplay(currentTurnPlayerIndex: number, opponentCount: number): UIGameDiceView {
        const topOpponentDiceView = this.opponentDiceViews[0]
        const midOpponentDiceView = this.opponentDiceViews[1]
        const bottomOpponentDiceView = this.opponentDiceViews[2]

        switch(currentTurnPlayerIndex) {
            case opponentCount - 1: return bottomOpponentDiceView
            case opponentCount - 2: return midOpponentDiceView
            case 0: return topOpponentDiceView
            default: return midOpponentDiceView
        }
    }

    private beginnerHintReceived(data: BeginnerHintFormat) {
        if(this.playerDiceView.parent == undefined) return
        if(data.type != GameAction.ClickedDice) return
        if(this.uiGameManager.gameController.currentState.actionState != ActionState.None) return

        const suggestToRollDice = this.uiGameManager.gameController.isMyTurn && this.uiGameManager.gameController.currentState.turnState == TurnState.Dice
        if(!suggestToRollDice) return

        this.destroyBeginnerHintView()
        Sound.playSound(Sound.SoundType.BeginnerHintActivated)
        this.beginnerHintView = new UIGameBeginnerHintView(this.uiGameManager, this.playerDiceView, {key: 'strings:game.beginnerTips.actionHints.description', options: {context: 'rollDice'}}, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.dice_6))
    }

    private destroyBeginnerHintView() {
        this.beginnerHintView?.destroy()
        this.beginnerHintView = undefined
    }

    protected setMyDiceViewVisibility(isVisible: boolean) {
        this.playerDiceView.visible = isVisible
    }

    private startDiceNumberAnimation(diceView: UIGameDiceView) {
        if(diceView.parent == undefined) return
        if(!this.uiGameManager.gameController.diceState.diceThrown) return

        if(this.beginnerModeHelper == undefined) return this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.DiceNumberAnimationEnd)
        this.beginnerModeHelper.startDiceNumberAnimation(diceView)
    }
}
